import { Head } from '@inertiajs/react'
import { PageLayout } from '@/components/landing/PageLayout'
import { PageHeader, SectionDivider } from '@/components/landing/PageHeader'

export default function Privacy() {
  return (
    <>
      <Head>
        <title>Privacy Policy — KonexZero</title>
        <meta name="description" content="How KonexZero collects, uses, and protects your data. We process only the minimum data needed to deliver realtime communication services." />
      </Head>
      <PageLayout>
      <div className="space-y-10">
        <PageHeader
          eyebrow="Legal · last updated 2026-03-12"
          title="Privacy Policy"
        />

        <SectionDivider />

        <div className="prose prose-neutral dark:prose-invert max-w-none space-y-8">
          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">1. Introduction</h2>
            <p className="text-muted-foreground leading-relaxed">
              KonexZero ("we", "us", or "our") operates the KonexZero platform, providing realtime
              voice, video, and screen sharing APIs. This Privacy Policy explains how we collect,
              use, disclose, and safeguard your information when you use our services.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">2. Information We Collect</h2>
            <h3 className="font-medium text-base mt-4">Account Information</h3>
            <p className="text-muted-foreground leading-relaxed">
              When you create an account, we collect your name, email address, organization name,
              and authentication credentials. This information is necessary to provide and manage
              your access to our services.
            </p>

            <h3 className="font-medium text-base mt-4">Usage Data</h3>
            <p className="text-muted-foreground leading-relaxed">
              We automatically collect information about how you interact with our APIs, including
              API call volumes, room creation and participant counts, session durations, and error
              rates. This data helps us operate, maintain, and improve our services.
            </p>

            <h3 className="font-medium text-base mt-4">Media Data</h3>
            <p className="text-muted-foreground leading-relaxed">
              KonexZero facilitates realtime media transmission between participants. Audio, video,
              and screen share streams are routed through our global edge network and are not
              stored or recorded by KonexZero unless you explicitly enable recording features. Media
              streams are encrypted in transit using DTLS-SRTP.
            </p>

            <h3 className="font-medium text-base mt-4">Technical Information</h3>
            <p className="text-muted-foreground leading-relaxed">
              We collect IP addresses, browser type, device information, and log data for security,
              fraud prevention, and service optimization purposes.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">3. How We Use Your Information</h2>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Provide, operate, and maintain our API services</li>
              <li>Process your transactions and manage your account</li>
              <li>Monitor usage for billing, capacity planning, and abuse prevention</li>
              <li>Send administrative communications (service updates, security alerts)</li>
              <li>Improve and optimize our platform performance</li>
              <li>Comply with legal obligations and enforce our terms</li>
              <li>Respond to support requests and communicate with you</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">4. Data Sharing</h2>
            <p className="text-muted-foreground leading-relaxed">
              We do not sell your personal information. We may share data with:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>
                <strong className="text-foreground">Infrastructure providers:</strong> Edge network
                providers (WebRTC relay, edge computing), cloud hosting providers for API infrastructure
              </li>
              <li>
                <strong className="text-foreground">Payment processors:</strong> To handle billing
                and subscription management
              </li>
              <li>
                <strong className="text-foreground">Legal authorities:</strong> When required by law
                or to protect our rights
              </li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">5. Data Security</h2>
            <p className="text-muted-foreground leading-relaxed">
              We implement industry-standard security measures including encryption in transit
              (TLS 1.3), encrypted storage, API key hashing, role-based access controls, and regular
              security audits. See our{' '}
              <a href="/security" className="text-primary hover:underline">
                Security page
              </a>{' '}
              for detailed information.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">6. Data Retention</h2>
            <p className="text-muted-foreground leading-relaxed">
              We retain your account information for as long as your account is active. Usage and
              analytics data is retained for up to 90 days in detailed form and in aggregated form
              thereafter. You may request deletion of your account and associated data at any time.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">7. Your Rights</h2>
            <p className="text-muted-foreground leading-relaxed">
              Depending on your jurisdiction, you may have the right to:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Access the personal data we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to or restrict certain processing</li>
              <li>Data portability</li>
              <li>Withdraw consent where processing is based on consent</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed">
              To exercise any of these rights, contact us at{' '}
              <a href="mailto:privacy@konexzero.com" className="text-primary hover:underline">
                privacy@konexzero.com
              </a>
              .
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">8. Cookies</h2>
            <p className="text-muted-foreground leading-relaxed">
              We use essential cookies for authentication and session management. We do not use
              third-party tracking or advertising cookies. Our dashboard uses session cookies
              managed by Devise for secure authentication.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">9. Changes to This Policy</h2>
            <p className="text-muted-foreground leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any
              material changes by posting the new policy on this page and updating the "Last
              updated" date. Continued use of our services after changes constitutes acceptance.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="font-heading text-xl font-semibold">10. Contact Us</h2>
            <p className="text-muted-foreground leading-relaxed">
              If you have questions about this Privacy Policy, please contact us at{' '}
              <a href="mailto:privacy@konexzero.com" className="text-primary hover:underline">
                privacy@konexzero.com
              </a>{' '}
              or visit our{' '}
              <a href="/contact" className="text-primary hover:underline">
                Contact page
              </a>
              .
            </p>
          </section>
        </div>
      </div>
    </PageLayout>
    </>
  )
}
